import { formatDateOnly } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "@/lib/public-icons";
import { useState } from "react";
import { StripePaymentSheet } from "@/components/app/payments/stripe-payment-sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpcPublic } from "@/lib/orpc";
import { PayInvoiceShell } from "@/routes/-components/pay-invoice-shell";
import { PayInvoiceUnavailable } from "@/routes/-components/pay-invoice-unavailable";
import { PayInvoiceNotice } from "@/routes/-components/pay-invoice-notice";
import type { PayOptions, PayMethod } from "@/components/app/payments/stripe-payment-sheet-types";

export const Route = createFileRoute("/pay/$token")({
  component: PublicPay,
  head: () => ({
    meta: [{ title: "Pay your invoice" }, { name: "robots", content: "noindex, nofollow" }],
  }),
});

const USD = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" });
const money = (cents: number) => USD.format(cents / 100);

/** The token in the URL is the whole authorization, so nothing tenant-identifying is sent; the
 * server reads the invoice, resident and building from the link row. The page never learns the
 * resident's full name, so a misdirected link is not a disclosure. */
function PublicPay() {
  const { token } = Route.useParams();
  const [paying, setPaying] = useState(false);
  const [settled, setSettled] = useState(false);

  const link = useQuery({
    ...orpcPublic.financials.payments.public.view.queryOptions({ input: { token } }),
    retry: false,
  });

  if (link.isPending) {
    return (
      <PayInvoiceShell>
        <Skeleton className="h-40 w-full" />
      </PayInvoiceShell>
    );
  }
  if (link.isError || !link.data) {
    /** Expired, cancelled and overused links say so, since the reader can ask for a fresh one. An
     * unresolved link shows nothing more: the server's wording is written for logs. */
    const refusal = link.error as { code?: string; message?: string } | null;
    return (
      <PayInvoiceUnavailable
        message={
          refusal?.code === "CONFLICT" && refusal.message
            ? refusal.message
            : "Please contact the community office and they will send a fresh link or take the payment over the phone."
        }
      />
    );
  }

  const view = link.data;
  if (settled) {
    return (
      <PayInvoiceNotice tone="ok" icon={<CheckCircle2 />} title="Thank you">
        Your payment has gone through. A receipt goes to the address this link was sent to, and the
        balance updates once it settles.
      </PayInvoiceNotice>
    );
  }

  const balance = view.balanceCents;
  const usable = view.onlinePaymentsEnabled && (view.methods.card || view.methods.ach);

  /* The same shape the portal and the office screen hand the sheet, built out
     of what this link already carries — the payer has no session, so nothing
     here may be read from one. */
  const options: PayOptions = {
    enabled: view.onlinePaymentsEnabled,
    reason: view.reason,
    card: view.methods.card,
    ach: view.methods.ach,
    allowPartialPayment: view.allowPartialPayment,
    minPaymentCents: view.minPaymentCents,
    cardFeeMode: view.cardFee ? "surcharge" : null,
    cardFeePercent: view.cardFee?.percent ?? null,
    cardFeeFixedCents: view.cardFee?.fixedCents ?? null,
    cardFeeLabel: view.cardFee?.label ?? null,
  };

  return (
    <PayInvoiceShell>
      <div className="space-y-5">
        <div className="space-y-1 text-center">
          <h1 className="font-greeting text-2xl text-balance">Pay your invoice</h1>
          <p className="text-muted-foreground text-sm">{view.facilityName}</p>
        </div>
        <div className="bg-sunken rounded-lg px-4 py-5 text-center">
          <p className="text-muted-foreground text-xs font-medium">Amount due</p>
          <p className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">
            {money(balance)}
          </p>
          <p className="text-muted-foreground mt-1 text-sm">
            Invoice {view.invoiceNumber} for {view.residentDisplayName}
            {view.dueDate ? `, due ${formatDateOnly(view.dueDate)}` : ""}
          </p>
        </div>

        {usable ? (
          <>
            <Button size="lg" className="w-full" onClick={() => setPaying(true)}>
              Pay {money(balance)}
            </Button>
            {view.cardFee ? (
              <p className="text-muted-foreground text-center text-xs">{view.cardFee.label}</p>
            ) : null}
            {view.signInForBank ? (
              <p className="text-muted-foreground text-center text-sm">
                To pay from your bank account, sign in to the family portal.
              </p>
            ) : null}
          </>
        ) : view.signInForBank && view.onlinePaymentsEnabled ? (
          <p className="text-muted-foreground text-center text-sm">
            This community takes bank payments from an account on file. Sign in to the family portal
            to pay from your bank account, or call the office.
          </p>
        ) : (
          <p className="text-muted-foreground text-center text-sm">
            {view.reason ??
              "This community is not set up to take payments online. Please call the office."}
          </p>
        )}
      </div>

      {/* The card is typed into Stripe's iframe and confirmed by Stripe.js in this browser, so the
          number never reaches this application. */}
      <StripePaymentSheet
        open={paying}
        onOpenChange={setPaying}
        title="Pay your invoice"
        subtitle={`Invoice ${view.invoiceNumber}, ${view.facilityName}`}
        balanceCents={balance}
        options={options}
        initiate={(amountCents: number, method: PayMethod) =>
          orpcPublic.financials.payments.public.payIntent.call({ token, amountCents, method })
        }
        pollStatus={(attemptId: string) =>
          orpcPublic.financials.payments.public.attempt.call({ token, attemptId })
        }
        onSettled={() => {
          setPaying(false);
          setSettled(true);
        }}
      />
    </PayInvoiceShell>
  );
}
